import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';

interface NotificationCenterProps {
    isOpen: boolean;
    onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ isOpen, onClose }) => {
    const {
        notifications,
        unreadCount,
        loading,
        loadNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification
    } = useNotification();
    const navigate = useNavigate();
    const panelRef = useRef<HTMLDivElement>(null);

    // Refresh notifications when panel opens
    useEffect(() => {
        if (isOpen) {
            loadNotifications();
        }
    }, [isOpen, loadNotifications]);

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, onClose]);

    const handleNotificationClick = useCallback(async (notificationId: string) => {
        await markAsRead(notificationId);
    }, [markAsRead]);

    const handleDelete = useCallback(async (e: React.MouseEvent, notificationId: string) => {
        e.stopPropagation();
        await deleteNotification(notificationId);
    }, [deleteNotification]);

    const handleViewAll = useCallback(() => {
        onClose();
        navigate('/notifications');
    }, [onClose, navigate]);

    const formatTimestamp = (timestamp: any): string => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'success':
                return (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 flex-shrink-0">
                        <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                );
            case 'warning':
                return (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-50 flex-shrink-0">
                        <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                );
            case 'error':
                return (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-50 flex-shrink-0">
                        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                );
            default:
                return (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 flex-shrink-0">
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                );
        }
    };

    return (
        <>
            {/* Slide-in animation for notification items */}
            <style>{`
                @keyframes notifSlideIn {
                    from { opacity: 0; transform: translateX(20px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                .notif-item {
                    animation: notifSlideIn 0.3s ease-out both;
                }
            `}</style>

            {/* Overlay */}
            <div
                className={`fixed inset-0 z-[99998] transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                    }`}
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
                onClick={onClose}
            />

            {/* Sliding Panel */}
            <div
                ref={panelRef}
                className={`fixed top-0 right-0 h-full w-full sm:w-[400px] z-[99999] transform transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
                style={{
                    backgroundColor: '#ffffff',
                    borderLeft: '1px solid #e5e7eb',
                    boxShadow: '-10px 0 40px rgba(0, 0, 0, 0.1)'
                }}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between px-5 py-4"
                    style={{ borderBottom: '1px solid #e5e7eb' }}
                >
                    <div className="flex items-center gap-3">
                        <h2 className="text-base font-semibold text-gray-900">Notifications</h2>
                        {unreadCount > 0 && (
                            <span className="inline-flex items-center justify-center rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-600 ring-1 ring-blue-200">
                                {unreadCount} new
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                            <button
                                onClick={() => markAllAsRead()}
                                className="text-[11px] font-medium text-gray-400 hover:text-gray-600 transition-colors duration-200 px-2 py-1 rounded-lg hover:bg-gray-50"
                            >
                                Mark all read
                            </button>
                        )}
                        <button
                            onClick={() => loadNotifications()}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-200"
                            title="Refresh"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </button>
                        <button
                            onClick={onClose}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-200"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Notification List */}
                <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100% - 120px)' }}>
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64 gap-3">
                            <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
                            <p className="text-xs text-gray-400">Loading notifications…</p>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 gap-4 px-8">
                            <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-50">
                                <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                            </div>
                            <div className="text-center">
                                <p className="text-sm font-medium text-gray-500">No notifications yet</p>
                                <p className="text-xs text-gray-400 mt-1">
                                    You'll see alerts for campaigns, contacts, and more here.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div>
                            {notifications.map((notification, index) => (
                                <div
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification.id)}
                                    className={`notif-item group relative px-5 py-3.5 cursor-pointer transition-all duration-200 hover:bg-gray-50 ${!notification.isRead ? 'bg-blue-50/50' : ''
                                        }`}
                                    style={{ borderBottom: '1px solid #f3f4f6', animationDelay: `${index * 0.05}s` }}
                                >
                                    <div className="flex gap-3">
                                        {getTypeIcon(notification.type)}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className={`text-sm leading-snug ${!notification.isRead
                                                    ? 'font-semibold text-gray-900'
                                                    : 'font-medium text-gray-600'
                                                    }`}>
                                                    {notification.title}
                                                </p>
                                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                                    {!notification.isRead && (
                                                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                                                    )}
                                                </div>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
                                                {notification.message}
                                            </p>
                                            <div className="flex items-center justify-between mt-1.5">
                                                <p className="text-[10px] text-gray-400">
                                                    {formatTimestamp(notification.createdAt)}
                                                </p>
                                                <button
                                                    onClick={(e) => handleDelete(e, notification.id)}
                                                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-all duration-200"
                                                    title="Delete notification"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="absolute bottom-0 left-0 right-0 px-5 py-3.5"
                    style={{
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: '#ffffff'
                    }}
                >
                    <button
                        onClick={handleViewAll}
                        className="w-full rounded-xl bg-gray-50 px-4 py-2.5 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all duration-200 flex items-center justify-center gap-2"
                    >
                        View all notifications
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </button>
                </div>
            </div>
        </>
    );
};

export default NotificationCenter;
